import React from 'react'

import { CloseIcon } from '@/assets/icons'
import { cn } from '@/helpers/cn'

import type { ChangeEvent, KeyboardEvent } from 'react'

interface ComponentProps {
  value: string
  placeholder?: string
  onChange: (value: string) => void
  onAction: () => void
  icon?: React.ElementType
  actionLabel?: React.ReactNode
  onClear?: () => void
  clearLabel?: string
  title?: React.ReactNode
  disabled?: boolean
}

export const SearchBarInput = React.forwardRef<HTMLInputElement, ComponentProps>(
  ({ value, placeholder, onChange, onAction, icon: Icon, actionLabel, onClear, clearLabel, title, disabled }, ref) => {
    const handleChange = React.useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value)
      },
      [onChange]
    )

    const handleKeyDown = React.useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          onAction()
        }
      },
      [onAction]
    )

    const isClearVisible = Boolean(onClear && value)

    return (
      <div className="relative w-full">
        {title && <div className="mb-1 font-bold text-base">{title}</div>}
        <input
          ref={ref}
          value={value}
          onChange={handleChange}
          type="text"
          placeholder={placeholder}
          className={cn(
            'h-10 w-full rounded-md border border-input-border-input p-2 pr-10 font-normal font-sans text-base text-body-text-alternative leading-6',
            actionLabel && 'pr-24',
            actionLabel && isClearVisible && 'pr-52'
          )}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />

        {actionLabel && (
          <div className="absolute right-0 bottom-0 flex h-10 items-center text-border-input">
            {isClearVisible && (
              <button
                type="button"
                onClick={onClear}
                disabled={disabled}
                className="flex cursor-pointer items-center gap-1 border-0 bg-transparent px-1 font-sans text-base text-inherit disabled:cursor-not-allowed disabled:opacity-50"
              >
                {clearLabel}
                <CloseIcon className="size-5" />
              </button>
            )}
            <button
              type="button"
              onClick={onAction}
              disabled={disabled}
              className="flex cursor-pointer items-center gap-1 border-0 bg-transparent px-3 font-sans text-base text-inherit disabled:cursor-not-allowed disabled:opacity-50"
            >
              {Icon && <Icon className="size-5" />}
              {actionLabel}
            </button>
          </div>
        )}

        {Icon && !actionLabel && (
          <Icon
            onClick={onAction}
            className={cn(
              'absolute top-0.75 right-1 m-2 size-5 cursor-pointer text-border-input',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          />
        )}
      </div>
    )
  }
)

SearchBarInput.displayName = 'SearchBarInput'
